
import { LabelConfig } from '../types';
import { getAppCheckHeaderOrThrow } from './firebase';
import { orderErrorFromException, orderErrorFromResponse } from './orderErrors';

export interface DriveOrderPayload {
  token: string;
  createdAt: string;
  name: string;
  className: string;
  quantity: number;
  svgContent: string;
  config: LabelConfig;
  returnUrl: string | null;
  /** Jazyk zákaznické části (cs, sk, pl, en, de, it, es, hu). */
  lang?: string;
  finalUrl?: string;
  file?: Blob;
  fileName?: string;
}

export interface DriveOrderResult {
  success: boolean;
  pdfFileName: string;
  previewFileName: string;
  previewUrl?: string;
}

/**
 * Sends order data to the backend /api/upload-order endpoint,
 * which uploads the SVG file to Google Drive.
 */
export async function sendOrderToDrive(payload: DriveOrderPayload): Promise<DriveOrderResult> {
  const formData = new FormData();
  formData.append('token', payload.token);
  formData.append('createdAt', payload.createdAt);
  formData.append('name', payload.name);
  formData.append('className', payload.className);
  formData.append('quantity', payload.quantity.toString());
  formData.append('returnUrl', payload.returnUrl || '');
  formData.append('finalUrl', payload.finalUrl || '');
  if (payload.lang) formData.append('lang', payload.lang);

  if (payload.file && payload.fileName) {
    formData.append('file', payload.file, payload.fileName);
  } else if (payload.svgContent) {
    const blob = new Blob([payload.svgContent], { type: 'image/svg+xml' });
    formData.append('file', blob, payload.fileName || `order_${payload.token}.svg`);
  }

  const fileSizeBytes = payload.file?.size ?? payload.svgContent?.length ?? 0;
  // Minimum 150s, plus 1s per 50 KB — covers slow connections (~400 kbps upload). The server may
  // queue the order behind others and needs up to 120s to render the PDF, so a shorter limit made
  // the app give up (and re-send) while the first attempt was still being processed.
  const timeoutMs = Math.max(150_000, Math.ceil(fileSizeBytes / 50_000) * 1_000);
  // The token is fetched before the upload timer starts, so a slow token never eats into the upload
  // time; failing to get one is reported as such (and not retried) instead of as a server error.
  let headers: Record<string, string>;
  try {
    headers = await getAppCheckHeaderOrThrow();
  } catch (err) {
    throw orderErrorFromException(err, timeoutMs);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch('/api/upload-order', {
      method: 'POST',
      headers,
      body: formData,
      signal: controller.signal,
    });
  } catch (err) {
    throw orderErrorFromException(err, timeoutMs);
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw orderErrorFromResponse(response.status, response.headers.get('Retry-After'), text);
  }

  return response.json();
}